import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useQueryClient } from "@tanstack/react-query";

import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
} from "../../postgrestApi/common";
import request from "../../request";
import type { UnitParam } from "./bill";

import { VITE_UTILITIES_BASE_URL } from "../../../env";
import { throwBackpackSuperagentResponseError } from "../errors";

export type Consumption = {
  id: number;
  billRowId: number;
  meterRowId: number;
  startDate: string;
  endDate: string;
  consumption: number;
  consumptionUnit: number;
  consumptionCost: number;
  totalCost: number;
  wasteWaterConsumption?: number | null;
  wasteWaterConsumptionCost?: number | null;
  peakDemand: number | null;
  peakDemandUnit: "kW" | "kVa" | null;
  peakDemandCost: number | null;
  meterDemand: number | null;
  peakOccurance: string | null;
  powerFactor: number | null;
  isCostOnly?: boolean;
};

const consumption = {
  // useQuery,
  useQueryAll,
  mutations: {
    usePost,
    // usePut,
    // useDelete,
  },
} as const;

export default consumption;

// GET

export type GetConsumption = Consumption & {
  insertedBy: number;
  insertedAt: string;
  updatedBy: number;
  updatedAt: string;
};

// endpoint requires {feature: "Utilities", access: "read"}
const getAllConsumption = (
  siteId: number,
  billId: number,
  unit: UnitParam,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  request
    .get(
      `${VITE_UTILITIES_BASE_URL}/sites/${siteId}/bills/${billId}/meter_consumption`
    )
    .set(checkFeatureAccessHeader)
    .query({ unit })
    .then(({ body }) => recursiveCamelCaseCipher(body) as GetConsumption[]);

export const UTILITY_BILL_CONSUMPTION_KEY = "getUtilityBillConsumption";
function useQueryAll(
  siteId: number,
  billId: number,
  options: { unit?: UnitParam; enabled?: boolean } = {
    enabled: false,
  }
) {
  const { unit = "default", enabled } = options;

  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getAllConsumption(siteId, billId, unit, checkFeatureAccessHeader),
    {
      queryKey: [UTILITY_BILL_CONSUMPTION_KEY, siteId, billId, unit],
      enabled,
    }
  );
}

// GET /sites/{site_id}/bills/{bill_id}/meter_consumption/{id}
// ^ not needed in frontend at the moment, but currently exists in service
// also supports unit query param w/ 'raw' | 'default' as accepted values

// POST

type PostMeterConsumptionRequest = Array<
  Partial<Omit<Consumption, "id" | "billRowId">>
>;

// endpoint requires {feature: "Utilities", access: "edit"}
const postUtilityBillMeterConsumption = (
  siteId: number,
  billId: number,
  consumptionRequest: PostMeterConsumptionRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  request
    .post(
      `${VITE_UTILITIES_BASE_URL}/sites/${siteId}/bills/${billId}/meter_consumption`
    )
    .set("Content-Type", "application/json")
    .set(checkFeatureAccessHeader)
    .send(recursiveSnakeCaseCipher(consumptionRequest))
    .then((res) => {
      if (res.status !== 200) {
        throw new Error(
          `Unable to create meter consumption records for site: ${siteId}, bill: ${billId}`
        );
      }
    })
    .catch(throwBackpackSuperagentResponseError);

function usePost(siteId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (
      {
        billId,
        consumptionItems,
      }: {
        billId: number;
        consumptionItems: PostMeterConsumptionRequest;
      },
      checkFeatureAccessHeader
    ) =>
      postUtilityBillMeterConsumption(
        siteId,
        billId,
        consumptionItems,
        checkFeatureAccessHeader
      ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [UTILITY_BILL_CONSUMPTION_KEY, siteId],
        }),
      onError: (e, { billId }) => {
        throw new Error(
          `Unable to create meter consumption records for site: ${siteId}, bill: ${billId}
        ${(e as Error)?.message || "Unknown Error"}`
        );
      },
    }
  );
}
